import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, CodeBracketIcon, DocumentTextIcon, PrinterIcon } from "@heroicons/react/24/outline";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { HsePageChrome } from "../../../../../components/dashboard/hse/HsePageChrome";
import { PMField } from "../../../../../components/utils/PMField";
import { FormikSaveBar } from "../../../../../components/utils/FormikSaveBar";
import { ResourceLoading } from "../../../../../components/utils/ResourceLoading";
import { FormDocumentBuilder } from "../../../../../components/dashboard/document-forms/FormDocumentBuilder";
import { FormDocumentJsonDialog } from "../../../../../components/dashboard/document-forms/FormDocumentJsonDialog";
import { parseImportedFormDocumentJson } from "../../../../../components/dashboard/document-forms/formDocumentImportUtils";
import { AIGenerator } from "../../../../../components/utils/AIGenerator";
import { aiGeneratorConfigs } from "../../../../../config/aiGeneratorConfigs";
import {
  createDocumentFormTemplate,
  fetchDocumentFormCategories,
  fetchDocumentFormTemplate,
  registerDocumentFormTemplateHse,
  fetchDocumentFormTemplatePdf,
  updateDocumentFormTemplate,
} from "../../../../../api/documentForms";
import { DisciplineCategoryChapterSelector } from "../../../../../components/dashboard/documents/DisciplineCategoryChapterSelector";

export const DocumentFormTemplatePage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(Boolean(id));
  const [post, setPost] = useState(null);
  const [categories, setCategories] = useState([]);
  const [isRegisteringHse, setIsRegisteringHse] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [hseDisciplineId, setHseDisciplineId] = useState("");
  const [hseCategoryId, setHseCategoryId] = useState("");
  const [hseChapterNumber, setHseChapterNumber] = useState("");

  useEffect(() => {
    fetchDocumentFormCategories({ active: true }).then((res) => {
      setCategories(res?.data || []);
    });
  }, []);

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    fetchDocumentFormTemplate(id).then((data) => {
      setPost(data?.id ? data : null);
      setIsLoading(false);
    });
  }, [id]);

  useEffect(() => {
    const hse = post?.hse_document;
    if (!hse?.id) return;
    setHseDisciplineId(hse.discipline?.id ? String(hse.discipline.id) : "");
    setHseCategoryId(hse.category?.id ? String(hse.category.id) : "");
    setHseChapterNumber(hse.chapter?.number ? String(hse.chapter.number) : "");
  }, [post?.hse_document]);

  const save = async (values) => {
    const payload = {
      title: values.title,
      description: values.description,
      active: values.active,
      number_prefix: values.number_prefix?.trim() || null,
      document_form_template_category_id: values.document_form_template_category_id || null,
      blocks: values.blocks || [],
    };

    const res = post?.id
      ? await updateDocumentFormTemplate(post.id, payload)
      : await createDocumentFormTemplate(payload);

    if (res?.errors) {
      toast.error(Array.isArray(res.errors) ? res.errors.join(", ") : t("documentForms.save_failed"));
      return;
    }

    toast.success(t("Saved"));
    setPost(res);
    if (!post?.id && res?.id) {
      navigate(`/dashboard/hse-ks/documents/templates/${res.id}`, { replace: true });
    }
  };

  const registerInHms = async () => {
    if (!post?.id) {
      toast.error(t("documentForms.hse.save_template_first"));
      return;
    }
    if (!hseCategoryId) {
      toast.error(t("documentForms.hse.category_required"));
      return;
    }
    setIsRegisteringHse(true);
    const res = await registerDocumentFormTemplateHse(post.id, {
      discipline_id: hseDisciplineId || undefined,
      category_id: hseCategoryId,
      chapter_number: hseChapterNumber || undefined,
    });
    setIsRegisteringHse(false);
    if (res?.errors) {
      toast.error(Array.isArray(res.errors) ? res.errors.join(", ") : t("documentForms.hse.register_failed"));
      return;
    }
    toast.success(res?.message || t("documentForms.hse.registered"));
    const updated = await fetchDocumentFormTemplate(post.id);
    if (updated?.id) setPost(updated);
  };

  const handlePrintTemplate = async () => {
    if (!post?.id) {
      toast.error(t("documentForms.templates.save_before_print"));
      return;
    }
    setIsPrinting(true);
    const res = await fetchDocumentFormTemplatePdf(post.id);
    setIsPrinting(false);
    if (res?.pdf_url) {
      window.open(res.pdf_url, "_blank", "noopener");
    } else {
      toast.error(t("documentForms.print_failed"));
    }
  };

  if (isLoading) {
    return (
      <HsePageChrome title={t("documentForms.templates.edit")}>
        <ResourceLoading />
      </HsePageChrome>
    );
  }

  const initialValues = {
    id: post?.id || null,
    title: post?.title || "",
    description: post?.description || "",
    number_prefix: post?.number_prefix || "",
    active: post?.active !== false,
    document_form_template_category_id: post?.document_form_template_category_id || "",
    blocks: post?.blocks || [],
  };

  return (
    <HsePageChrome
      title={post?.id ? t("documentForms.templates.edit") : t("documentForms.templates.new")}
      subtitle={t("documentForms.templates.editor_hint")}
    >
      <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Link
            to="/dashboard/hse-ks/documents/templates"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            {t("Back")}
          </Link>
          {post?.id ? (
            <button
              type="button"
              disabled={isPrinting}
              onClick={handlePrintTemplate}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
            >
              <PrinterIcon className="h-4 w-4" />
              {isPrinting ? t("Loading") : t("documentForms.templates.print")}
            </button>
          ) : null}
          <Link
            to="/dashboard/hse-ks/documents/form-archive"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
          >
            {t("documentForms.archive.title")}
          </Link>
        </div>

        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={Yup.object({
            title: Yup.string().required(t("Required")),
          })}
          onSubmit={save}
        >
          {(formik) => (
            <Form>
              <div className="mb-6 rounded-[2rem] border border-slate-200 bg-white px-6 py-6 shadow-sm">
                <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                  <DocumentTextIcon className="h-4 w-4" />
                  {t("documentForms.templates.meta")}
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <PMField name="title" type="text" label={t("Title")} {...formik} />
                  <PMField
                    name="number_prefix"
                    type="text"
                    label={t("documentForms.templates.number_prefix")}
                    placeholder={t("documentForms.templates.number_prefix_placeholder")}
                    {...formik}
                  />
                  <p className="md:col-span-2 text-xs text-slate-500">
                    {t("documentForms.templates.number_prefix_hint")}
                  </p>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      {t("documentForms.categories.label")}
                    </label>
                    <select
                      value={formik.values.document_form_template_category_id || ""}
                      onChange={(e) =>
                        formik.setFieldValue(
                          "document_form_template_category_id",
                          e.target.value ? Number(e.target.value) : ""
                        )
                      }
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    >
                      <option value="">{t("documentForms.categories.uncategorized")}</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <PMField
                      name="description"
                      label={t("Description")}
                      multiline
                      rows={2}
                      {...formik}
                    />
                  </div>
                </div>

                {post?.id ? (
                  <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4">
                    <div className="text-sm font-semibold text-slate-900">
                      {t("documentForms.hse.title")}
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{t("documentForms.hse.description")}</p>

                    <div className="mt-4 space-y-4">
                      <DisciplineCategoryChapterSelector
                        disciplineId={hseDisciplineId}
                        categoryId={hseCategoryId}
                        chapterNumber={hseChapterNumber}
                        onDisciplineChange={setHseDisciplineId}
                        onCategoryChange={(value) => {
                          setHseCategoryId(value);
                          setHseChapterNumber("");
                        }}
                        onChapterChange={setHseChapterNumber}
                        required
                      />
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        disabled={isRegisteringHse || !hseCategoryId}
                        onClick={registerInHms}
                        className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                      >
                        {isRegisteringHse
                          ? t("Loading")
                          : post?.hse_document?.id
                            ? t("documentForms.hse.update_placement")
                            : t("documentForms.hse.register")}
                      </button>
                      {post?.hse_document?.id ? (
                        <Link
                          to={`/dashboard/hse-ks/documents/${post.hse_document.id}`}
                          className="inline-flex text-sm font-semibold text-blue-700 hover:text-blue-900"
                        >
                          {t("documentForms.hse.open_document")} →
                        </Link>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="mb-4 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setJsonDialogOpen(true)}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
                >
                  <CodeBracketIcon className="h-4 w-4" />
                  {t("documentForms.json.open_dialog")}
                </button>
                <AIGenerator
                  module="document_form_template"
                  context={{
                    template_title: formik.values.title || "",
                    template_description: formik.values.description || "",
                    category: categories.find(
                      (c) => c.id === formik.values.document_form_template_category_id
                    )?.name || "",
                  }}
                  fieldsToGenerate={["blocks_json"]}
                  onGenerate={(generatedFields, meta) => {
                    const raw = generatedFields?.blocks_json;
                    if (raw == null || typeof raw !== "string" || !String(raw).trim()) {
                      toast.error(t("documentForms.json.ai_parse_error"));
                      return;
                    }
                    try {
                      const parsed = parseImportedFormDocumentJson(raw);
                      const applyMode = meta?.generationOptions?.apply_mode || "replace";
                      if (applyMode === "append") {
                        formik.setFieldValue("blocks", [
                          ...(formik.values.blocks || []),
                          ...parsed.blocks,
                        ]);
                      } else {
                        formik.setFieldValue("blocks", parsed.blocks);
                      }
                      if (parsed.title) formik.setFieldValue("title", parsed.title);
                      if (parsed.description) {
                        formik.setFieldValue("description", parsed.description);
                      }
                      toast.success(
                        t("documentForms.json.ai_apply_success", { count: parsed.blocks.length })
                      );
                    } catch (err) {
                      const code = err?.code || "unknown";
                      const key = `documentForms.json.error_${code}`;
                      const msg = t(key);
                      toast.error(msg !== key ? msg : t("documentForms.json.ai_parse_error"));
                    }
                  }}
                  config={aiGeneratorConfigs.document_form_template}
                  buttonLabel={t("documentForms.json.generate_with_ai")}
                  className="!rounded-full !border-slate-300 !px-4 !py-2 !text-sm !font-semibold"
                />
              </div>

              <FormDocumentBuilder
                blocks={formik.values.blocks}
                onChange={(blocks) => formik.setFieldValue("blocks", blocks)}
              />

              <FormikSaveBar {...formik} disabled={formik.isSubmitting} />

              <FormDocumentJsonDialog
                open={jsonDialogOpen}
                onClose={() => setJsonDialogOpen(false)}
                blocks={formik.values.blocks}
                title={formik.values.title}
                description={formik.values.description}
                onApply={({ blocks, title, description, mode }) => {
                  if (mode === "append") {
                    formik.setFieldValue("blocks", [...(formik.values.blocks || []), ...blocks]);
                  } else {
                    formik.setFieldValue("blocks", blocks);
                  }
                  if (title) formik.setFieldValue("title", title);
                  if (description != null && description !== "") {
                    formik.setFieldValue("description", description);
                  }
                }}
              />
            </Form>
          )}
        </Formik>
      </div>
    </HsePageChrome>
  );
};
